import { loadAssetUrls, uploadAsset } from "./repository";
import type { AssetFile } from "./types";

type Listener = () => void;

/**
 * Manages asset URL map with subscribe pattern for React integration.
 */
export class AssetManager {
  private urlMap: Record<string, string> = {};
  private listeners: Set<Listener> = new Set();

  constructor(public directory?: FileSystemDirectoryHandle) {}

  setDirectory(directory: FileSystemDirectoryHandle) {
    this.directory = directory;
  }

  getState = () => this.urlMap;
  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  async load(assets: AssetFile[]) {
    if (!this.directory || !assets.length) {
      this.urlMap = {};
      this.notify();
      return;
    }
    const result = await loadAssetUrls(this.directory, assets);
    this.urlMap = result.urls;
    this.notify();
  }
  async upload(file: File) {
    if (!this.directory) {
      throw new Error("No directory handle available for upload.");
    }
    const result = await uploadAsset(this.directory, file);
    this.urlMap = { ...this.urlMap, [result.asset.id]: result.blobUrl };
    this.notify();
    return result;
  }
  set(assetId: string, url: string) {
    this.urlMap = { ...this.urlMap, [assetId]: url };
    this.notify();
  }
  /**
   * Gets a file from OPFS by asset ID.
   * Returns null if the file is not found.
   */
  async getFile(assetId: string): Promise<Blob | null> {
    if (!this.directory) {
      return null;
    }
    try {
      const fileHandle = await this.directory.getFileHandle(assetId);
      return await fileHandle.getFile();
    } catch {
      return null;
    }
  }
  remove(assetId: string) {
    const url = this.urlMap[assetId];
    if (url) {
      URL.revokeObjectURL(url);
    }
    const { [assetId]: _, ...remaining } = this.urlMap;
    this.urlMap = remaining;
    this.notify();
  }
  revokeAll() {
    for (const url of Object.values(this.urlMap)) {
      URL.revokeObjectURL(url);
    }
    this.urlMap = {};
    this.notify();
  }

  private notify() {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
